import { useState } from 'react'

import type { DualControlResult } from '@/api/types'
import { MoneyDisplay } from '@/components/money-display'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { formatMoney, type Pesewas } from '@/lib/money'

import { DualControlNotice, DualControlOutcome } from './dual-control'
import { LoanExposureBanner } from './exposure-banner'
import { LoanPrerequisitesPanel } from './prerequisites-panel'
import { LOAN_PERMISSIONS, useLoanPermissions } from './permissions'
import { loanPaths, useLoanNavigate } from './loan-paths'
import {
  AmountField,
  AppliedOutcome,
  NoteField,
  NumberField,
  OperationForm,
} from './operation-form'
import type { CycleOperationContext } from './operation-screen'
import { useApproveRound, useRecordAppraisal } from './mutations'

/**
 * The two halves of a credit decision: the appraisal that establishes the facts, and the approval
 * that commits the institution to them.
 *
 * They are separate screens because they are separate acts by separate people. The domain refuses
 * an approval with no appraisal on file (`loans.appraisal_required`) and refuses an approver who
 * is the applicant (`loans.self_approval`), and neither of those is a rule a browser can enforce —
 * so the screens state them rather than trying to.
 */

export function AppraisalForm({ cycle, accountId, cycleId }: CycleOperationContext) {
  const navigate = useLoanNavigate()
  const appraise = useRecordAppraisal(cycleId, accountId)

  const [notes, setNotes] = useState(cycle.appraisalNotes ?? '')
  const [recommended, setRecommended] = useState<Pesewas | null>(cycle.recommendedAmountMinor)
  const [bureau, setBureau] = useState(cycle.bureauInquiryReference ?? '')
  const [done, setDone] = useState(false)

  const currency = cycle.summary.currencyCode
  const trimmedNotes = notes.trim()

  return (
    <OperationForm
      title="Record the appraisal"
      description="The finding the decision will be made on. Recording it moves the round to a decision."
      submitLabel="Record appraisal"
      isPending={appraise.isPending}
      disabled={trimmedNotes === ''}
      error={appraise.isError ? appraise.error : null}
      onDone={() => navigate(loanPaths.cycle(accountId, cycleId))}
      outcome={
        done ? (
          <AppliedOutcome title="Appraisal recorded">
            The round is now with whoever decides it. Nothing has been committed to the member —
            an appraisal is a finding, not an approval.
          </AppliedOutcome>
        ) : null
      }
      onSubmit={() => {
        appraise.mutate(
          {
            notes: trimmedNotes,
            recommendedAmountMinor: recommended,
            bureauInquiryReference: bureau.trim() === '' ? null : bureau.trim(),
          },
          { onSuccess: () => setDone(true) },
        )
      }}
      footnote="An appraisal can be recorded more than once while the round is still being appraised. The last one stands."
    >
      <NoteField
        id="appraisal-notes"
        label="Appraisal notes"
        required
        multiline
        autoFocus
        value={notes}
        onChange={setNotes}
        placeholder="What the officer found: the business, the cash flow, what was seen at the stall."
        hint="Written for the person deciding this round, and for whoever reads the file in three years."
      />

      <AmountField
        id="appraisal-recommended"
        label="Recommended amount"
        required={false}
        currency={currency}
        value={recommended}
        onChange={setRecommended}
        hint={
          <>
            Leave it empty to recommend the amount applied for,{' '}
            {formatMoney(cycle.appliedAmountMinor, { currency })}. Empty is a different statement
            from zero, and is sent as such.
          </>
        }
      />

      <NoteField
        id="appraisal-bureau"
        label="Credit bureau enquiry reference"
        value={bureau}
        onChange={setBureau}
        placeholder="XDS-2026-…"
        hint="The enquiry this appraisal relied on, if one was made."
      />
    </OperationForm>
  )
}

/**
 * Approval, which freezes the terms.
 *
 * The approved amount is the figure everything downstream is computed from: the schedule, the
 * security deposit re-check, the growth cap on the next round. It may be less than was applied
 * for and never more, which the server refuses as `loans.approved_amount_exceeds_applied`.
 */
export function ApproveForm({ cycle, account, accountId, cycleId }: CycleOperationContext) {
  const navigate = useLoanNavigate()
  const permissions = useLoanPermissions()
  const approve = useApproveRound(cycleId, accountId)

  const suggested = cycle.recommendedAmountMinor ?? cycle.appliedAmountMinor
  const [amount, setAmount] = useState<Pesewas | null>(suggested)
  const [instalments, setInstalments] = useState(String(cycle.terms.instalmentCount))
  const [note, setNote] = useState('')
  const [result, setResult] = useState<DualControlResult | null>(null)

  const currency = cycle.summary.currencyCode
  const instalmentCount = Number.parseInt(instalments, 10)
  const valid =
    amount !== null &&
    amount > 0 &&
    Number.isInteger(instalmentCount) &&
    instalmentCount > 0 &&
    amount <= cycle.appliedAmountMinor

  return (
    <div className="space-y-4">
      {account ? <LoanExposureBanner customerId={account.customerId} /> : null}

      {cycle.appraisalNotes === null ? (
        <Alert tone="warning">
          <AlertTitle>No appraisal has been recorded</AlertTitle>
          <AlertDescription>
            The server refuses a decision on a round nobody has appraised
            (<span className="font-mono text-xs">loans.appraisal_required</span>). Record the
            appraisal first — this form will be refused as it stands.
          </AlertDescription>
        </Alert>
      ) : null}

      <OperationForm
        title="Approve the round"
        description="Approving freezes the terms at the approved amount. Amending the product afterwards cannot reach them."
        submitLabel="Approve"
        isPending={approve.isPending}
        disabled={!valid}
        error={approve.isError ? approve.error : null}
        onDone={() => navigate(loanPaths.cycle(accountId, cycleId))}
        notice={
          <DualControlNotice
            action="Approving a round"
            approvePermission={LOAN_PERMISSIONS.approveCountersign}
            canCountersign={permissions.canCountersignApproval}
          />
        }
        outcome={
          result ? (
            <DualControlOutcome
              result={result}
              awaitingDescription="The approval is stored and the round is still undecided."
              appliedDescription="The round is approved and its terms are frozen. It can now be disbursed — by somebody other than you."
            />
          ) : null
        }
        onSubmit={() => {
          if (!valid) {
            return
          }

          approve.mutate(
            {
              approvedAmountMinor: amount,
              instalmentCount,
              note: note.trim() === '' ? null : note.trim(),
            },
            { onSuccess: setResult },
          )
        }}
        footnote="Whoever approves a round may not disburse it. Three people is the minimum before cash leaves the institution."
      >
        <div className="grid gap-4 sm:grid-cols-2">
          <AmountField
            id="approve-amount"
            label="Approved amount"
            currency={currency}
            value={amount}
            onChange={setAmount}
            autoFocus
            hint={
              <>
                Applied for {formatMoney(cycle.appliedAmountMinor, { currency })}
                {cycle.recommendedAmountMinor === null
                  ? ''
                  : `; appraiser recommended ${formatMoney(cycle.recommendedAmountMinor, { currency })}`}
                . It may be less than was applied for, never more.
              </>
            }
          />

          <NumberField
            id="approve-instalments"
            label="Instalments"
            value={instalments}
            onChange={setInstalments}
            min={1}
            hint="The product's range is checked by the server, against the product this round was written on."
          />
        </div>

        {amount !== null && amount > cycle.appliedAmountMinor ? (
          <Alert tone="danger">
            <AlertTitle>More than was applied for</AlertTitle>
            <AlertDescription>
              The member asked for{' '}
              <MoneyDisplay
                value={cycle.appliedAmountMinor}
                currency={currency}
                className="inline"
              />
              . Approving above that is refused. A larger amount is a new application.
            </AlertDescription>
          </Alert>
        ) : null}

        <NoteField
          id="approve-note"
          label="Note"
          value={note}
          onChange={setNote}
          hint="Kept on the round with the decision. Not shown to the member."
        />

        {account ? (
          <LoanPrerequisitesPanel
            customerId={account.customerId}
            productId={account.productId}
            principalMinor={amount ?? undefined}
            title="Gates at this amount"
          />
        ) : null}
      </OperationForm>
    </div>
  )
}
